/**
 * Race-day carb estimate — SHARED between the server (training-plan library
 * bootstrap builds the trimmed policy) and the customer-app wizard (imports via
 * `@shared`, like `carbCoverage.ts`), so the wizard's projected numbers can't
 * drift from the engine's band math.
 *
 * Deliberately import-free: the client bundles this file, so it must not pull
 * the zod policy schema or any server code. `FuelEstimatePolicy` is the wire
 * subset of `FuelPlanPolicy` the estimate needs; the server resolves the run
 * sport-cap up front (`sportCarbCapForPlannerSport`) so the planner-sport →
 * family ladder stays server-only.
 *
 * The math mirrors the engine's race-day path exactly — `durationTier` +
 * `adjustCarbRangeForSession` (intensity = high, elevation unknown → skipped)
 * + the sport-cap clamp + sex-bias band position from `phaseTargets`. Ramp is
 * intentionally absent: race day always runs at 100%.
 */

export type FuelEstimatePolicy = {
  /** `policy.durationTiers` — inclusive upper bounds, last tier open-ended. */
  durationTiers: Array<{
    maxMinutesInclusive: number | null;
    carbMinGPerHr: number;
    carbMaxGPerHr: number;
  }>;
  /**
   * C4 — `policy.intensity.carbAdjust` (races are high intensity, so only the
   * high value/pct is read here). Mode-aware:
   *   - grams: additive high g/hr delta (legacy replay).
   *   - percent: high % compounded with the experience % (C5) below —
   *     intensity → experience — and applied off the base band once.
   */
  carbAdjust:
    | { mode: "grams"; high: number }
    | { mode: "percent"; highPct: number };
  /**
   * C5 — experience (gut-level) carb %, percent-mode only. Anonymous / no
   * profile → omit (or pass 0). Grams-mode replay ignores it.
   */
  experiencePct?: number | null;
  weightKg: {
    lightMaxExclusive: number;
    heavyMinExclusive: number;
    lightMultiplier: number;
    heavyMultiplier: number;
  };
  sexBias: {
    femaleRangePosition: number;
    maleRangePosition: number;
    unknownRangePosition: number;
  };
  /** Resolved `sportCarbCapForPlannerSport("run")` — all library plans are runs. */
  runCapGPerHr: number;
  allowOverCapRecommendations: boolean;
};

export type RaceDayEstimateInput = {
  /** Goal finish time in minutes (pace-band `goalMinutes`). */
  goalMinutes: number;
  /** `null`/`"non-binary"`/`"other"` all resolve to the unknown band position. */
  sex: string | null;
  /** Body weight in kg; `null` skips the light/heavy multiplier. */
  weightKg: number | null;
};

/**
 * Projected race-day during-carbs rate (g/hr) for the athlete, or `null` when
 * the policy block is unavailable (older server / degraded load) so callers
 * can fall back rather than show a wrong number.
 */
export function estimateRaceDayCarbsPerHour(
  policy: FuelEstimatePolicy | null | undefined,
  input: RaceDayEstimateInput
): number | null {
  if (!policy || policy.durationTiers.length === 0) return null;
  if (!Number.isFinite(input.goalMinutes) || input.goalMinutes <= 0) return null;

  const tiers = policy.durationTiers;
  const tier =
    tiers.find(
      (t) => t.maxMinutesInclusive == null || input.goalMinutes <= t.maxMinutesInclusive
    ) ?? tiers[tiers.length - 1];

  // C4/C5 — mode-aware high-intensity adjustment, matching
  // adjustCarbRangeForSession. Grams mode: additive delta (legacy replay).
  // Percent mode: high % and experience % compounded sequentially (intensity →
  // experience, mirroring applyCarbPcts — inlined here because this file must
  // stay import-free for the client bundle), rounded once; weight stacks after.
  // Multiply before divide so integer bands/pcts stay exact at .5 boundaries.
  let min: number;
  let max: number;
  if (policy.carbAdjust.mode === "grams") {
    min = Math.max(0, tier.carbMinGPerHr + policy.carbAdjust.high);
    max = Math.max(min, tier.carbMaxGPerHr + policy.carbAdjust.high);
  } else {
    const iPct = 100 + policy.carbAdjust.highPct;
    const ePct = 100 + (policy.experiencePct ?? 0);
    min = Math.max(0, Math.round((tier.carbMinGPerHr * iPct * ePct) / 10000));
    max = Math.max(min, Math.round((tier.carbMaxGPerHr * iPct * ePct) / 10000));
  }

  if (input.weightKg) {
    let mult = 1;
    if (input.weightKg < policy.weightKg.lightMaxExclusive) mult = policy.weightKg.lightMultiplier;
    else if (input.weightKg > policy.weightKg.heavyMinExclusive)
      mult = policy.weightKg.heavyMultiplier;
    min = Math.round(min * mult);
    max = Math.round(max * mult);
  }

  if (!policy.allowOverCapRecommendations) {
    min = Math.min(policy.runCapGPerHr, min);
    max = Math.min(policy.runCapGPerHr, max);
  }

  const position =
    input.sex === "male"
      ? policy.sexBias.maleRangePosition
      : input.sex === "female"
        ? policy.sexBias.femaleRangePosition
        : policy.sexBias.unknownRangePosition;
  return Math.round(min + position * (max - min));
}
